import Seo from "@/components/Seo";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowUpRight,
  Newspaper,
  Video,
  Camera,
  Share2,
  Mail,
  Globe,
  Mic2,
  Calendar,
  BarChart3,
  Sparkles,
  Handshake,
  Users,
  Award,
  Search,
  Signal,
  Compass,
  Quote,
  Check,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const glide = (delay = 0, duration = 0.5) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration, delay, ease: EASE },
});
// For elements always in the viewport on load (like the hero).
const rise = (delay = 0, duration = 0.5) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration, delay, ease: EASE },
});

// ─────────────────────────────────────────────────────────────
// Pull quote - large italic serif quote that fades in and stays
// pinned as the reader scrolls past.
// ─────────────────────────────────────────────────────────────
function PullQuote({ children, testId }) {
  return (
    <section
      data-testid={testId}
      className="py-24 md:py-32 bg-slate-50 border-y border-slate-200 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative mx-auto max-w-[1100px] px-5 md:px-10">
        <motion.div {...glide()}>
          <Quote className="h-10 w-10 text-accent-blue mb-8" />
          <p className="font-heading font-black text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
            {children}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Branding vs Marketing split - hover-emphasised comparison.
// ─────────────────────────────────────────────────────────────
function BrandingMarketingSplit() {
  const [hover, setHover] = useState(null);

  const brandingPoints = [
    "Who are you?",
    "What do you stand for?",
    "Why should people trust you?",
    "What makes you different?",
    "Why should someone remember you?",
  ];
  const brandingOutcomes = [
    "Creates perception.",
    "Builds credibility.",
    "Develops reputation.",
    "Creates trust.",
  ];

  const marketingActivities = [
    { icon: Newspaper, label: "Industry news articles" },
    { icon: Sparkles, label: "Project case studies" },
    { icon: Video, label: "Video production" },
    { icon: Signal, label: "Drone filming" },
    { icon: Share2, label: "Social media" },
    { icon: Mail, label: "Newsletters" },
    { icon: Globe, label: "Websites" },
    { icon: Camera, label: "Photography" },
    { icon: Calendar, label: "Industry events" },
    { icon: Mic2, label: "Podcasts" },
    { icon: BarChart3, label: "Digital campaigns" },
  ];

  return (
    <section
      data-testid="branding-vs-marketing"
      className="py-24 md:py-32 bg-white"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div {...glide()} className="max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              Two disciplines. One system.
            </span>
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl leading-[0.98] tracking-tighter text-slate-900">
            Branding and marketing
            <br />
            are <span className="text-accent-blue italic">not the same.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-4 md:gap-6">
          {/* BRANDING */}
          <motion.div
            {...glide(0.05)}
            data-testid="branding-column"
            onMouseEnter={() => setHover("branding")}
            onMouseLeave={() => setHover(null)}
            className={`relative rounded-3xl border p-8 md:p-10 transition-all duration-300 ${
              hover === "marketing"
                ? "border-slate-200 opacity-60"
                : "border-slate-900 bg-slate-900 text-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <Compass
                className={`h-6 w-6 ${
                  hover === "marketing" ? "text-slate-400" : "text-accent-blue"
                }`}
              />
              <span
                className={`text-xs font-heading font-semibold uppercase tracking-widest ${
                  hover === "marketing" ? "text-slate-500" : "text-accent-blue"
                }`}
              >
                Branding
              </span>
            </div>
            <h3
              className={`mt-5 font-heading font-black text-3xl md:text-4xl leading-tight tracking-tight ${
                hover === "marketing" ? "text-slate-500" : "text-white"
              }`}
            >
              Answers who you are.
            </h3>
            <p
              className={`mt-4 text-sm md:text-base leading-relaxed ${
                hover === "marketing" ? "text-slate-400" : "text-white/70"
              }`}
            >
              Branding is the foundation. It is what people believe about you
              before you ever pick up the phone.
            </p>

            <div
              className={`mt-8 pt-6 border-t ${
                hover === "marketing" ? "border-slate-200" : "border-white/10"
              }`}
            >
              <div
                className={`text-[10px] font-heading font-semibold uppercase tracking-widest ${
                  hover === "marketing" ? "text-slate-400" : "text-white/50"
                }`}
              >
                Branding answers…
              </div>
              <ul className="mt-4 space-y-2.5">
                {brandingPoints.map((q) => (
                  <li
                    key={q}
                    className={`text-sm md:text-base leading-snug ${
                      hover === "marketing" ? "text-slate-400" : "text-white"
                    }`}
                  >
                    {q}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={`mt-8 pt-6 border-t ${
                hover === "marketing" ? "border-slate-200" : "border-white/10"
              }`}
            >
              <div
                className={`text-[10px] font-heading font-semibold uppercase tracking-widest ${
                  hover === "marketing" ? "text-slate-400" : "text-accent-blue"
                }`}
              >
                Branding creates…
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {brandingOutcomes.map((o) => (
                  <span
                    key={o}
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
                      hover === "marketing"
                        ? "bg-slate-100 text-slate-500"
                        : "bg-white/10 text-white"
                    }`}
                  >
                    <Check className="h-3 w-3 text-accent-blue" />
                    {o}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* MARKETING */}
          <motion.div
            {...glide(0.1)}
            data-testid="marketing-column"
            onMouseEnter={() => setHover("marketing")}
            onMouseLeave={() => setHover(null)}
            className={`relative rounded-3xl border p-8 md:p-10 transition-all duration-300 ${
              hover === "branding"
                ? "border-slate-200 bg-white opacity-60"
                : "border-slate-200 bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <Share2 className="h-6 w-6 text-accent-blue" />
              <span className="text-xs font-heading font-semibold uppercase tracking-widest text-accent-blue">
                Marketing
              </span>
            </div>
            <h3 className="mt-5 font-heading font-black text-3xl md:text-4xl leading-tight tracking-tight text-slate-900">
              Answers how you say it.
            </h3>
            <p className="mt-4 text-slate-600 text-sm md:text-base leading-relaxed">
              Marketing is the vehicle. It is every activity that carries your
              brand into the marketplace.
            </p>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="text-[10px] font-heading font-semibold uppercase tracking-widest text-slate-500">
                Marketing includes…
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {marketingActivities.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2 text-xs md:text-sm text-slate-800"
                  >
                    <Icon className="h-3.5 w-3.5 text-accent-blue flex-shrink-0" />
                    <span className="truncate">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                Marketing doesn&apos;t create the brand.
                <br />
                <span className="font-heading font-bold text-slate-900">
                  It communicates it.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Reveal-on-scroll counter list - used for "How we create brand
// value" activities.
// ─────────────────────────────────────────────────────────────
function BrandValueList() {
  const items = [
    "Articles demonstrate expertise.",
    "Project updates build confidence.",
    "Videos capture attention.",
    "Campaigns increase visibility.",
  ];
  return (
    <section
      data-testid="brand-value"
      className="py-24 md:py-32 bg-white border-t border-slate-200"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 md:gap-16">
        <motion.div {...glide()} className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              How we create brand value
            </span>
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tighter text-slate-900">
            Individually, they create impact.
            <br />
            <span className="text-accent-blue italic">
              Together, they build a brand.
            </span>
          </h2>
          <p className="mt-6 text-slate-600 text-lg leading-relaxed">
            Every piece of content has a purpose. Articles demonstrate
            expertise. Project updates build confidence. Videos capture
            attention. Campaigns increase visibility. Together, they create a
            brand that stands above the competition.
          </p>
        </motion.div>

        <div className="lg:col-span-7">
          <ul className="divide-y divide-slate-200 border-y border-slate-200">
            {items.map((line, i) => (
              <motion.li
                key={line}
                {...glide(0.05 + i * 0.04)}
                className="flex items-center gap-5 py-5 md:py-6 group"
              >
                <span className="font-mono text-xs text-slate-400 w-8 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 font-heading font-bold text-lg md:text-2xl text-slate-900 tracking-tight leading-tight">
                  {line}
                </span>
                <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-accent-blue transition-colors" />
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Sticky "Success looks like…" checklist
// ─────────────────────────────────────────────────────────────
function SuccessMeasures() {
  const measures = [
    { icon: Search, label: "More people recognising your company" },
    { icon: Globe, label: "More website visitors" },
    { icon: Signal, label: "Greater search visibility" },
    { icon: BarChart3, label: "Higher engagement" },
    { icon: Mail, label: "Increased newsletter exposure" },
    { icon: Share2, label: "Stronger social presence" },
    { icon: Users, label: "More conversations" },
    { icon: Calendar, label: "More invitations" },
    { icon: Award, label: "Better recruitment" },
    { icon: Handshake, label: "More partnership opportunities" },
    { icon: Newspaper, label: "More enquiries" },
    { icon: Sparkles, label: "Greater trust" },
  ];
  return (
    <section
      data-testid="success-measures"
      className="py-24 md:py-32 bg-slate-950 text-white dark-section relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div {...glide()} className="max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              Measuring success
            </span>
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl leading-[0.98] tracking-tighter">
            We don&apos;t measure clicks.
            <br />
            <span className="text-accent-blue italic">
              We measure recognition.
            </span>
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
            Because recognition comes before opportunity. Every metric below is
            a leading indicator of the frameworks, contracts and partnerships
            that will follow.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {measures.map(({ icon: Icon, label }, i) => (
            <motion.div
              key={label}
              {...glide(i * 0.03, 0.4)}
              className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 backdrop-blur"
            >
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-accent-blue/10 text-accent-blue flex-shrink-0">
                <Icon size={16} />
              </span>
              <span className="text-sm md:text-base text-white/90">
                {label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// The philosophy manifesto - 4 short lines revealing one by one.
// ─────────────────────────────────────────────────────────────
function PhilosophyManifesto() {
  const lines = [
    { text: "Branding creates trust.", accent: false },
    { text: "Trust creates opportunity.", accent: false },
    { text: "Marketing communicates that trust.", accent: false },
    {
      text: "Consistency turns it into business growth.",
      accent: true,
    },
  ];
  return (
    <section
      data-testid="philosophy-manifesto"
      className="py-24 md:py-36 bg-white"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 text-center">
        <motion.div {...glide()} className="inline-flex items-center gap-3">
          <span className="h-px w-12 bg-accent-blue" />
          <span className="label-eyebrow text-accent-blue">Our philosophy</span>
          <span className="h-px w-12 bg-accent-blue" />
        </motion.div>

        <div className="mt-12 space-y-3 md:space-y-6">
          {lines.map((line, i) => (
            <motion.p
              key={line.text}
              {...glide(0.1 + i * 0.15, 0.6)}
              className={`font-heading font-black text-4xl md:text-6xl lg:text-7xl leading-[0.96] tracking-tighter ${
                line.accent ? "text-accent-blue italic" : "text-slate-900"
              }`}
            >
              {line.text}
            </motion.p>
          ))}
        </div>

        <motion.p
          {...glide(0.85, 0.5)}
          className="mt-16 md:mt-20 text-slate-600 text-base md:text-lg max-w-3xl mx-auto leading-relaxed"
        >
          A one-off article, video or social media post is unlikely to generate
          immediate sales. Likewise, even a well-executed marketing campaign
          shouldn&apos;t be judged solely on whether it results in a direct
          sale. In the highways and infrastructure sector, purchasing decisions
          are often made over months or even years, and are built on trust,
          reputation and familiarity. The purpose of a campaign is to increase
          your brand awareness, demonstrate your expertise and ensure your
          business is recognised when opportunities arise. Any reputable
          marketing agency should be honest about this and set realistic
          expectations from the outset.
        </motion.p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Progress bar that fills as the reader moves through the page.
// ─────────────────────────────────────────────────────────────
function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop || document.body.scrollTop;
      const height = h.scrollHeight - h.clientHeight;
      setProgress(height > 0 ? (scrolled / height) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 h-0.5 z-40 pointer-events-none"
    >
      <div
        className="h-full bg-accent-blue transition-[width] duration-100 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
export default function OurApproach() {
  const videoRef = useRef(null);

  // iOS Safari autoplay is strict:
  //   • `muted` MUST be a hard attribute at render (React honours it, but we
  //     use `defaultMuted` too as some older iOS builds need it).
  //   • `playsInline` MUST be present.
  //   • DO NOT call `.play()` programmatically before user interaction, and
  //     DO NOT touch `playbackRate` before the video has actually started -
  //     both can pause the video silently on iOS.
  // We wait for the `onPlaying` event before slowing playback, and keep the
  // markup as plain as possible.
  const handlePlaying = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.playbackRate !== 0.6) v.playbackRate = 0.6;
  };

  return (
    <main data-testid="our-approach-page" className="bg-white">
      <Seo
        title="Our approach - branding creates demand, marketing captures it"
        description="Why the strongest highways and infrastructure brands aren't built through advertising alone. HighwaysIndustry.com's philosophy: consistent visibility, credibility and trust win frameworks, tenders and long-term contracts."
        keywords="highways branding, infrastructure marketing philosophy, long-term brand building, highways PR, highways content marketing, tender-winning marketing"
        path="/our-approach"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Branding creates demand. Marketing captures it.",
          "description": "The philosophy behind every campaign HighwaysIndustry.com builds. Why long-term brand building wins frameworks, tenders and preferred supplier status in highways.",
          "author": { "@type": "Organization", "name": "HighwaysIndustry.com" },
          "publisher": { "@type": "Organization", "name": "HighwaysIndustry.com", "logo": { "@type": "ImageObject", "url": "https://whyhighwaysindustry.com/highways-industry-mark.png" } },
          "image": "https://whyhighwaysindustry.com/og-image.jpg",
          "mainEntityOfPage": "https://whyhighwaysindustry.com/our-approach",
        }}
      />
      <ReadingProgress />

      {/* ─────────── HERO ─────────── */}
      <section
        data-testid="approach-hero"
        className="dark-section relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden text-white"
        style={{ backgroundColor: "#050a19" }}
      >
        {/* Cinematic slow-motion tunnel light-trails loop.
            Muted + playsInline + no programmatic play() = iOS-safe. */}
        <video
          ref={(el) => {
            videoRef.current = el;
            // React has a long-standing bug where `muted` isn't always set on
            // the DOM element even when the prop is true. Setting it via ref
            // guarantees iOS honours the muted-autoplay rule.
            if (el) el.muted = true;
          }}
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onPlaying={handlePlaying}
          src="/videos/approach-hero.mp4"
          poster="/images/approach-hero-poster.jpg"
        />
        {/* Dark gradient overlay for legibility */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/70 to-slate-950/95"
        />
        {/* Blueprint dot grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
            backgroundSize: "44px 44px",
          }}
        />
        {/* Radial cyan glow behind the headline */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 55%, rgba(34,211,238,0.18) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.div
            {...rise()}
            className="inline-flex items-center gap-3"
          >
            <span className="h-px w-12 bg-cyan-400" />
            <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-cyan-300">
              Our approach
            </span>
          </motion.div>

          <motion.h1
            {...rise(0.08, 0.5)}
            data-testid="approach-headline"
            className="mt-6 font-heading font-black text-5xl md:text-7xl lg:text-[8rem] leading-[0.9] tracking-tighter text-white max-w-6xl"
          >
            Branding creates demand.
            <br />
            <span className="text-cyan-300 italic">
              Marketing captures it.
            </span>
          </motion.h1>

          <motion.p
            {...rise(0.18, 0.5)}
            className="mt-10 text-white/80 text-lg md:text-2xl leading-relaxed max-w-3xl"
          >
            The strongest brands in highways and infrastructure aren&apos;t
            built through advertising alone. They&apos;re built through
            consistent visibility, credibility and trust.
          </motion.p>

          <motion.div {...rise(0.28)} className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/positioning-review"
              data-testid="approach-hero-cta"
              className="inline-flex items-center gap-2 bg-cyan-400 text-slate-950 hover:bg-cyan-300 px-6 py-3.5 text-sm font-bold uppercase tracking-widest transition-colors rounded-full"
            >
              Book a discovery call <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href="#philosophy"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-cyan-300 hover:text-cyan-300 text-white px-6 py-3.5 text-sm font-bold uppercase tracking-widest transition-colors rounded-full"
            >
              Read the philosophy
            </a>
          </motion.div>
        </div>
      </section>

      {/* ─────────── OPENING ARGUMENT ─────────── */}
      <section
        data-testid="approach-opener"
        id="philosophy"
        className="py-20 md:py-28 bg-slate-50 border-y border-slate-200 scroll-mt-20"
      >
        <div className="mx-auto max-w-[900px] px-5 md:px-10 space-y-8">
          <motion.p
            {...glide()}
            className="font-heading font-bold text-2xl md:text-3xl text-slate-900 leading-snug tracking-tight"
          >
            In highways and infrastructure, contracts are rarely won because
            someone saw a single advert.
          </motion.p>
          <motion.p
            {...glide(0.08)}
            className="text-slate-700 text-lg md:text-xl leading-relaxed"
          >
            They&apos;re won because decision-makers already recognise your
            company, understand your capabilities and trust your reputation
            when opportunities arise.
          </motion.p>
          <motion.p
            {...glide(0.16)}
            className="text-slate-700 text-lg md:text-xl leading-relaxed"
          >
            That&apos;s where we come in. We help businesses become recognised,
            remembered and respected through strategic brand-building
            campaigns that position them in front of the right audience,
            consistently.
          </motion.p>
          <motion.p
            {...glide(0.24)}
            className="text-slate-700 text-lg md:text-xl leading-relaxed"
          >
            Marketing is simply the vehicle we use to communicate that brand.
          </motion.p>
        </div>
      </section>

      {/* ─────────── BRANDING vs MARKETING ─────────── */}
      <BrandingMarketingSplit />

      {/* ─────────── PULL QUOTE #1 ─────────── */}
      <PullQuote testId="pull-quote-1">
        We don&apos;t promise overnight success.{" "}
        <span className="text-accent-blue italic">
          We build brands that become recognised, trusted and remembered.
        </span>
      </PullQuote>

      {/* ─────────── THE BIGGEST MISCONCEPTION ─────────── */}
      <section
        data-testid="misconception"
        className="py-24 md:py-32 bg-white"
      >
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <motion.div {...glide()} className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                The biggest misconception
              </span>
            </div>
            <h2 className="mt-4 font-heading font-black text-4xl md:text-5xl leading-[0.98] tracking-tighter text-slate-900">
              Most businesses ask
              <br />
              <span className="text-slate-400 italic">the wrong question.</span>
            </h2>
          </motion.div>

          <div className="lg:col-span-7 space-y-8">
            <motion.div {...glide(0.08)} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-8">
              <div className="text-[10px] font-heading font-semibold uppercase tracking-widest text-red-500">
                The wrong question
              </div>
              <p className="mt-3 font-heading font-bold text-2xl md:text-3xl text-slate-500 line-through decoration-red-400 decoration-2 leading-snug">
                &ldquo;How many sales will this article generate?&rdquo;
              </p>
            </motion.div>

            <motion.div {...glide(0.16)} className="rounded-2xl border-2 border-accent-blue bg-white p-6 md:p-8 shadow-sm">
              <div className="text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue">
                The better question
              </div>
              <p className="mt-3 font-heading font-bold text-2xl md:text-3xl text-slate-900 leading-snug">
                &ldquo;How much stronger will our brand become if our customers
                see us every week for the next twelve months?&rdquo;
              </p>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Because that&apos;s exactly how trust is built.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─────────── WHY BRANDING MATTERS ─────────── */}
      <section
        data-testid="why-branding-matters"
        className="py-24 md:py-32 bg-slate-950 text-white dark-section relative overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <motion.div {...glide()} className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Why branding matters in highways
              </span>
            </div>
            <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl leading-[0.98] tracking-tighter">
              Highways firms
              <br />
              don&apos;t sell{" "}
              <span className="text-accent-blue italic">£20 products.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              They win the kind of decisions that are influenced by reputation.
              Companies that consistently communicate their projects,
              innovations, people and achievements remain visible while
              competitors remain silent. When an opportunity appears,
              they&apos;re already recognised.
            </p>
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-3">
            {[
              "Frameworks",
              "Tenders",
              "Preferred supplier status",
              "Long-term contracts",
              "Partnerships",
              "Recruitment opportunities",
            ].map((item, i) => (
              <motion.div
                key={item}
                {...glide(i * 0.05, 0.4)}
                className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6 md:p-8"
              >
                <span className="font-mono text-[10px] text-accent-blue tracking-widest">
                  0{i + 1}
                </span>
                <p className="mt-4 font-heading font-bold text-lg md:text-2xl text-white tracking-tight leading-tight">
                  {item}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── HOW WE CREATE BRAND VALUE ─────────── */}
      <BrandValueList />

      {/* ─────────── PULL QUOTE #2 ─────────── */}
      <PullQuote testId="pull-quote-2">
        Any reputable marketing agency should tell you this:{" "}
        <span className="text-accent-blue italic">
          one article won&apos;t transform your business. Consistent visibility
          over time will.
        </span>
      </PullQuote>

      {/* ─────────── MEASURING SUCCESS ─────────── */}
      <SuccessMeasures />

      {/* ─────────── PHILOSOPHY MANIFESTO ─────────── */}
      <PhilosophyManifesto />

      {/* ─────────── FINAL CTA ─────────── */}
      <section
        data-testid="approach-final-cta"
        className="py-24 md:py-32 bg-slate-50 border-t border-slate-200"
      >
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 text-center">
          <motion.div {...glide()}>
            <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.96] tracking-tighter text-slate-900 max-w-3xl mx-auto">
              Ready to become
              <br />
              <span className="text-accent-blue italic">
                the recognised name in your sector?
              </span>
            </h2>
            <p className="mt-8 text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
              One discovery call. No pitch decks. Just an honest conversation
              about where your brand is now, and where it could be in twelve
              months.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link
                to="/positioning-review"
                data-testid="approach-cta-review"
                className="inline-flex items-center gap-2 bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
              >
                Book a discovery call <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/what-we-do"
                data-testid="approach-cta-services"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-accent-blue text-slate-900 px-7 py-4 text-sm font-bold uppercase tracking-widest transition-colors rounded-full"
              >
                See what we do
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
